import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting } from 'app/shared/lib/essette-site';
import { EssCollapseCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { ElliDataServiceMock } from 'app/ext/elli/elli-service';
import { I18N } from 'aurelia-i18n';
import { applySort } from 'app/shared/lib/sorting';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { GaugeColors } from 'app/shared/elements/gauges/gauge-colors';
import { GaugeColorSegment } from 'app/shared/elements/gauges/gauge-segments';
import { asColor } from 'app/shared/elements/gauges/as-color';

// TODO [Matt] This is a selector for a generic scrollable container element. We
//  use it to scroll to the correct section in the page. Generally, this is the
//  window. However, if we put this in a modal, its the .ess-dialog-content. I'm
//  leaving it as this specific value for now, but in the future we should make
//  this a generic concept.
const SCROLLABLE_CONTAINER_SELECTOR = '.ess-dialog-content';

const LEVEL_SEGMENTS: GaugeColorSegment[] = GaugeColors.GYR.map((color, threshold) => Object.assign({ color, threshold }));
const GAPS_IN_CARE_SEGMENTS: GaugeColorSegment[] = [
    { color: GaugeColors.Green, threshold: 0 },
    { color: GaugeColors.Yellow, threshold: 13 },
    { color: GaugeColors.Red, threshold: 26 }
]

@autoinject()
export class ElliMemberProfileViewModel extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected opened = new Set<number>([0, 1, 2, 3, 4]);

    protected memberId!: string;
    protected data!: any;
    protected thresholds: Record<string, GaugeColorSegment[]> | null = null;

    protected concurrentRiskFactorsSorting = new Sorting();

    // INFO [Matt] Format 'name|code - desc ^ code - desc'
    protected parseCoding = (undercoding: string): any => {
        const [name, codes] = undercoding.split('|');
        return { name };
    }

    protected getLevelColor = (value: number) => asColor(value, LEVEL_SEGMENTS);
    protected getGapsInCareColor = (value: number) => asColor(value, GAPS_IN_CARE_SEGMENTS);


    ////****************** Element References ***************************************////
    protected $collapse!: EssCollapseCustomAttribute;
    protected $collapseElement!: HTMLElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(protected service: ElliDataServiceMock, protected i18n: I18N, ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
    }

    protected async activate({ memberId }: { memberId: string }): Promise<void | false> {
        this.memberId = memberId;
        this.loadElliMemberData(memberId);
    }

    ////****************** Events ***************************************************////
    protected async scrollAndOpen(index: number) {
        const section = this.$collapseElement.children[index] as HTMLElement;
        if(!section) {
            return;
        }
        // INFO [Matt] Wait for the section to open if it isn't opened already.
        await new Promise((resolve) => {
            if(this.isSectionOpen(index) === true) {
                resolve();
            } else {
                this.$collapseElement.addEventListener('openend', () => resolve(), { once: true });
                this.$collapse.open(index);
            }
        });

        const scrollContainer = this.$collapseElement.closest(SCROLLABLE_CONTAINER_SELECTOR) || window;
        scrollContainer.scrollTo({ top: section.offsetTop, behavior: 'smooth' })
    }

    protected processThresholds(thresholdRecords: any[]): Record<string, GaugeColorSegment[]> {
        return thresholdRecords
            .sort((a, b) => a.riskLvl - b.riskLvl)
            .reduce((thresholds, { riskType, uthreshold, riskLvl }) => {
                const segments = thresholds[riskType] = thresholds[riskType] || [];
                segments.push({
                    threshold: uthreshold,
                    color: GaugeColors.GYR[riskLvl]
                });
                return thresholds;
            }, {});
    }

    ////****************** Functions ************************************************////
    @apiCall
    protected async loadElliMemberData(memberId: string) {
        try {
            this.state.beginLoad();
            const { result, isSuccessful } = await this.service.getMemberData(memberId);
            if(isSuccessful) {
                if(result.utilthreshold) {
                    this.thresholds = this.processThresholds(result.utilthreshold);
                }
                this.data = result;
            }
        }
        catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    protected concurrentRiskValue(riskLevel: number) {
        return 100 / 2 ** (2 - riskLevel);
    }

    protected includeOpioids({ riskCategory }: { riskCategory: string }) {
        return riskCategory === 'OPIOIDS';
    }

    protected excludeOpioids({ riskCategory }: { riskCategory: string }) {
        return riskCategory !== 'OPIOIDS';
    }

    protected getOpioidsAlertRisk(opioidRisks: any[]) {
        return opioidRisks.reduce(((val, { riskLevel }) => val + riskLevel), 0)
    }

    protected getOpioidsAlertColor(opioidRisks: any[]) {
        return this.getLevelColor(
            Math.max(...opioidRisks.map(({ riskLevel }) => riskLevel))
        );
    }

    protected isSectionOpen(index: number) {
        const section = this.$collapseElement.children[index];
        if(!section) {
            return null;
        }
        return section.classList.contains('active');
    }

    protected async applySort(event: SortChangeEvent) {
        this.concurrentRiskFactorsSorting = applySort(this.concurrentRiskFactorsSorting, event);
    }
}
